import { useEffect, useState, useMemo } from 'react'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'

import type { DialogProps } from '@wartek-id/dialog-v1'
import { useToast } from '@wartek-id/toast'

import { assetUrl } from 'utils/url'
import {
  API_PATH_PELAKSANAAN_PROGRESS,
  useAPIPostPelaksanaanAppealRequest,
} from 'api/kinerja/useAPIKinerja'
import { ConfirmationDialog } from 'app/KinerjaShared/components/ConfirmationDialog'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'

import type {
  TPelaksanaanAppealableStep,
  TStatePelaksanaan,
} from 'types/kinerja-pelaksanaan'

type RequestDialogProps = Omit<DialogProps, 'children'> & {
  progressStep: TStatePelaksanaan
  appealStep: TPelaksanaanAppealableStep
  sortedSteps: Array<TStatePelaksanaan>
  sortedResetableSteps: Array<TStatePelaksanaan>
  title?: string
  renderStepCard: (step: TStatePelaksanaan) => React.ReactNode
}

export function RequestDialog({
  isOpen,
  progressStep,
  appealStep,
  onClose,
  sortedSteps,
  sortedResetableSteps,
  title = 'Ajukan perubahan ke atasan. Berikut hal yang terdampak:',
  renderStepCard,
  ...dialogProps
}: RequestDialogProps) {
  const [activeContent, setActiveContent] = useState<
    'CONFIRMATION' | 'SUCCESS'
  >('CONFIRMATION')
  const toast = useToast()
  const queryClient = useQueryClient()

  // ----------------------------------------------------
  // Resolve tipe value
  // ----------------------------------------------------
  const { tipe } = useKinerjaTipe()

  // ----------------------------------------------------
  // Reset dialog upon dismissal
  // ----------------------------------------------------
  useEffect(() => {
    if (!isOpen) {
      setActiveContent('CONFIRMATION')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // ----------------------------------------------------
  // Resolve steps to reset
  // ----------------------------------------------------
  const stepsToReset: Array<TStatePelaksanaan> = useMemo(() => {
    const appealStepOrder = sortedSteps.indexOf(appealStep)
    const progressStepOrder = sortedSteps.indexOf(progressStep)

    // Appeal step must be before the current step
    if (appealStepOrder < progressStepOrder) {
      // Only include steps between appeal step and current step (inclusive)
      let slicedSteps = sortedSteps.slice(appealStepOrder, progressStepOrder)

      // Filter out steps that are not resetable
      slicedSteps = slicedSteps.filter((step) =>
        sortedResetableSteps.includes(step)
      )

      return slicedSteps
    }
    return []
  }, [appealStep, progressStep, sortedSteps, sortedResetableSteps])

  // ----------------------------------------------------
  // Handle appeal mutation
  // ----------------------------------------------------
  const appealMutation = useAPIPostPelaksanaanAppealRequest()

  const handleConfirm = () => {
    appealMutation.mutate(
      { appealState: appealStep, tipe },
      {
        onSuccess: () => {
          setActiveContent('SUCCESS')
        },
        onError: () => {
          toast({
            message: 'Gagal lanjut. Coba lagi.',
          })
        },
      }
    )
  }

  const handleSuccessDismiss = () => {
    onClose?.()
    queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS)
  }

  const sharedDialogProps = {
    ...dialogProps,
    isOpen,
    onClose: onClose as () => void,
    closeOnOverlayClick: false,
    autoFocus: false,
  }

  if (activeContent === 'CONFIRMATION') {
    return (
      <ConfirmationDialog
        {...sharedDialogProps}
        size="md"
        title={title}
        confirmCta="Ajukan perubahan"
        cancelCta="Kembali"
        hasCloseButton={false}
        hasTickConfirmation
        tickConfirmationText="Saya setuju untuk mengulang pengisian"
        isLoading={appealMutation.isLoading}
        onConfirm={handleConfirm}
        onCancel={() => onClose?.()}
        mainContent={
          <div
            className={clsx(
              'grid gap-4',
              stepsToReset.length < 2 ? 'grid-cols-1' : 'grid-cols-2'
            )}
          >
            {stepsToReset.map((step) => renderStepCard(step))}
          </div>
        }
      />
    )
  }

  return (
    <ConfirmationDialog
      {...sharedDialogProps}
      title="Perubahan sedang diajukan ke atasan"
      description="Status pengajuannya dapat dilihat di halaman Notifikasi atau cek lagi halaman ini nanti."
      confirmCta="Saya mengerti"
      cancelCta="Kembali"
      hasCloseButton={false}
      topContent={
        <img
          src={assetUrl('/illustrations/img-time-document.png')}
          width={84}
          height={84}
          alt=""
        />
      }
      onConfirm={handleSuccessDismiss}
      onCancel={() => onClose?.()}
    />
  )
}
